import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography
} from '@mui/material';

import TablePaginationActions from '../../../../../components/utils/TablePaginationActions';
import useTablePaginationInit from '../../../../../components/hooks/useTablePaginationInit';
import TableTimeStamps from '../../../../../components/utils/TableTimeStamps';
import Copyable from '../../../../../components/utils/Copyable';
import TopBar from '../../../../../components/utils/TopBar';
import NA from '../../../../../components/utils/NA';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/Delete';

import {
  GetComplianceVectorsBIZcoreTD,
  DeleteComplianceVectorBIZcoreTD,
  UpdateComplianceVectorBIZcoreTD
} from '../../../../../redux/actions/business/core/tradeDefencesActions';

import ComplienceVectorModal from './ComplienceVectorModal';
import ConfirmationModal from '../../../../../components/modals/ConfirmationModal';

import global from "../../../../../global.scss";

const ComplienceVectorPage = () => {
  const dispatch = useDispatch();
  const state = useSelector(state => state["business.core.tradeDefence"]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [complienceVectorModalOpen, setComplienceVectorModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [update, setUpdate] = useState(null);

  const [complianceVectorsList, setComplianceVectorsList] = useState([]);
  const [complianceVectorsTotal, setComplianceVectorsTotal] = useState(0);

  const [complienceVectorMenuOpen, setComplienceVectorMenuOpen] = useState(false);
  const [complienceVectorMenuAnchor, setComplienceVectorMenuAnchor] = useState(null);

  const [deleteConfirmationModalOpen, setDeleteConfirmationModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const [
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage
  ] = useTablePaginationInit();

  const tableHeadingsList = [
    {
      label: "Id",
      sort: "id",
    },
    {
      label: "Name",
      sort: "name",
    },
    {
      label: "Vector code",
      sort: "vector_code",
    },
    {
      label: "Pillar number",
      sort: "pillar_number",
    },
    {
      label: "Is deterministic",
      sort: "is_deterministic",
    },
    {
      label: "Order",
      sort: "order",
    },
    {
      label: "Is active",
      sort: "is_active",
    },
    {
      label: "Data sources",
      sort: null,
    },
    {
      label: "Description",
      sort: null,
    },
    {
      label: "Time stamps",
      sort: "created_at",
    },
    {
      label: "",
      sort: null
    }
  ];

  const handleSort = (value) => {
    setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    setSort(value);
  };

  useEffect(() => {
    handleGetComplianceVectors();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, sort, sortDirection]);

  useEffect(() => {
    try {
      setComplianceVectorsList(state.complianceVectorsList.records || []);
      setComplianceVectorsTotal(state.complianceVectorsList.total || 0);
    } catch (err) {}
  }, [state]);

  async function handleGetComplianceVectors(customLoading) {
    let query = "";
    if (search) query += `&search=${search}`;
    if (sort) query += `&ordering=${sortDirection === "asc" ? '' : '-'}${sort}`;
    dispatch(GetComplianceVectorsBIZcoreTD(page + 1, rowsPerPage, query, true, customLoading ? customLoading : setLoading));
  };

  const handleToggleActive = (row) => {
    const loadingSetter = (val) => setTogglingId(val === true ? row.id : null);
    dispatch(UpdateComplianceVectorBIZcoreTD(row.id, { is_active: !row.is_active }, loadingSetter)).then(() => {
      handleGetComplianceVectors(() => {});
    });
  };

  const handleDelete = () => {
    setDeleteConfirmationModalOpen(false);
    dispatch(DeleteComplianceVectorBIZcoreTD(deleteId, setDeleting)).then(() => {
      handleGetComplianceVectors().then(() => {
        setDeleteId(null);
      });
    });
  };

  return (
    <Box>

      <ComplienceVectorModal
        open={complienceVectorModalOpen}
        setOpen={setComplienceVectorModalOpen}
        title={modalTitle}
        update={update}
        handleGet={handleGetComplianceVectors}
      />

      <ConfirmationModal
        open={deleteConfirmationModalOpen}
        setOpen={setDeleteConfirmationModalOpen}
        title={"Delete " + modalTitle}
        onCancel={() => setDeleteId(null)}
        onConfirm={handleDelete}
      />

      <TopBar
        title={"Compliance vectors"}
        search={search}
        onSearchChange={(e) => {
          setSearch(e.target.value);
        }}
        button1Text={"Add compliance vector"}
        button1OnClick={() => {
          setUpdate({
            defaultMode: 1
          });
          setModalTitle("Add compliance vector");
          setComplienceVectorModalOpen(!complienceVectorModalOpen);
        }}
      />

      {
        loading
        ?
        <Box height={"300px"} className="flexCenterCenterRow">
          <CircularProgress />
        </Box>
        :
          complianceVectorsList.length === 0
          ?
          <Box height={"300px"} className="flexCenterCenterRow">
            <Typography>No compliance vector found</Typography>
          </Box>
          :
          <Table className="block">
            <TableHead>
              <TableRow>
                {
                  tableHeadingsList.map((heading, headingIndex) => (
                    heading.sort === null
                    ?
                    <TableCell
                      key={headingIndex}>{heading.label}</TableCell>
                    :
                    <TableCell
                      key={headingIndex}>
                      <TableSortLabel
                        active={sort === heading.sort ? true : false}
                        direction={sortDirection}
                        onClick={() => {
                          handleSort(heading.sort);
                        }}
                      >
                        {heading.label}
                      </TableSortLabel>
                    </TableCell>
                  ))
                }
              </TableRow>
            </TableHead>
            <TableBody>
              {
                complianceVectorsList.map((row, index) => (
                  <TableRow
                    key={index}>
                    <TableCell>
                      <Typography
                        variant='caption'
                        color={"grey"}>
                        <Copyable>
                          {row.id || <NA />}
                        </Copyable>
                      </Typography>
                    </TableCell>
                    <TableCell>{row.name || <NA />}</TableCell>
                    <TableCell>
                      {row.vector_code ?? "—"}
                    </TableCell>
                    <TableCell>{row.pillar_number ?? <NA />}</TableCell>
                    <TableCell>
                      {
                        row.is_deterministic === true
                        ? <Chip size="small" color="success" label="Yes" />
                        : row.is_deterministic === false
                          ? <Chip size="small" color="error" label="No" />
                          : <NA />
                      }
                    </TableCell>
                    <TableCell>{row.order ?? <NA />}</TableCell>
                    <TableCell>
                      {
                        togglingId === row.id
                        ? <CircularProgress size={20} sx={{ display: 'block', margin: '2px 4px' }} />
                        : <Tooltip title={row.is_active ? 'Disable globally' : 'Enable globally'}>
                            <Switch
                              checked={!!row.is_active}
                              onChange={() => handleToggleActive(row)}
                              disabled={togglingId !== null}
                            />
                          </Tooltip>
                      }
                    </TableCell>
                    <TableCell>
                      {
                        (row.data_sources || []).length > 0
                        ? <Chip size="small" label={(row.data_sources || []).length} />
                        : <NA />
                      }
                    </TableCell>
                    <TableCell>
                      <Typography variant='caption'>
                        {row.description || <NA />}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <TableTimeStamps
                        createdAt={row.created_at}
                        updatedAt={row.updated_at}
                      />
                    </TableCell>
                    <TableCell>
                      {
                        row.id === deleteId && deleting
                        ?
                        <CircularProgress size={26} sx={{ margin: "7px" }} />
                        :
                        <IconButton
                          onClick={(e) => {
                            setComplienceVectorMenuAnchor(e.currentTarget);
                            setComplienceVectorMenuOpen(true);
                            setUpdate(row);
                            setModalTitle(row.name || "");
                            setDeleteId(row.id);
                          }}>
                          <MoreVertIcon sx={{ color: global['secondary_light'] }} />
                        </IconButton>
                      }
                    </TableCell>
                  </TableRow>
                ))
              }
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  sx={{ borderBottom: 0 }}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]}
                  colSpan={11}
                  count={complianceVectorsTotal}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: {
                      'aria-label': 'rows per page'
                    },
                    native: true
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          </Table>
      }
      <Menu
        open={complienceVectorMenuOpen}
        anchorEl={complienceVectorMenuAnchor}
        onClose={() => setComplienceVectorMenuOpen(false)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem
          onClick={() => {
            setModalTitle("Edit " + modalTitle);
            setUpdate({
              ...update,
              defaultMode: 2
            });
            setComplienceVectorMenuOpen(false);
            setComplienceVectorModalOpen(!complienceVectorModalOpen);
          }}>
          <Box
            className="flexCenter_Row"
            gap={"10px"}>
            <EditOutlinedIcon />
            Edit
          </Box>
        </MenuItem>
        {
          update?.company_prefs_count === 0
          &&
          <MenuItem
            onClick={() => {
              setComplienceVectorMenuOpen(false);
              setDeleteConfirmationModalOpen(true);
            }}>
            <Box
              className="flexCenter_Row"
              gap={"10px"}>
              <DeleteIcon />
              Delete
            </Box>
          </MenuItem>
        }
      </Menu>
    </Box>
  );
};

export default ComplienceVectorPage;
